import type { DisplayConfig } from '../models/metricDefinitions.ts';
import type { HourGroup } from '../models/staffMetrics.ts';
import { currencyService } from '../services/currency.ts';
import { dateService } from '../services/date.ts';

export const formatValue = (value: number | Date | null, config: DisplayConfig): string => {
  if (config.formatAsDate) {
    if (value === null) {
      return 'Keine Angabe';
    }
    if (value instanceof Date) {
      return dateService.formatDate(value);
    }
    return String(value);
  }

  const numericValue = typeof value === 'number' ? value : 0;

  if (config.formatAsCurrency) {
    return currencyService.formatNumberToCurrency(numericValue);
  }

  const roundedValue = config.decimalPlaces !== undefined ? Number(numericValue.toFixed(config.decimalPlaces)) : numericValue;

  const formatted = roundedValue.toLocaleString('de-DE');

  if (config.unit) {
    return `${formatted} ${config.unit}`;
  }

  return formatted;
};

export const formatSubValue = (value: number, config: DisplayConfig): string => formatValue(value, { ...config, unit: config.subUnit });

// PMA hours are read in Unterrichtsstunden with the clock hours below, VMA hours only in clock hours.
// The unit is left out, as the table names it once in its header (see GROUP_HOURS_UNIT_HINT).
export const formatGroupHours = (group: HourGroup, hours: number, lessons: number): { value: string; subText?: string } => {
  const format = (value: number) => formatValue(value, { label: '', decimalPlaces: 2 });

  return group === 'pedagogical' ? { value: format(lessons), subText: format(hours) } : { value: format(hours) };
};

export const GROUP_HOURS_UNIT_HINT: Record<HourGroup, string> = {
  pedagogical: 'Angaben in Unterrichtsstunden (UStd.), darunter in Zeitstunden.',
  non_pedagogical: 'Angaben in Zeitstunden.'
};

export const formatValueWithTarget = (value: number | Date | null, target: number | '?', config: DisplayConfig): string => {
  const formattedValue = formatValue(value, config);
  const formattedTarget = target === '?' ? '?' : formatValue(target, config);

  return `${formattedValue}/${formattedTarget}`;
};
